import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import Logo from "../components/Logo";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function MagicLogin() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [error, setError] = useState("");
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        const token = new URLSearchParams(window.location.search).get("token");
        if (!token) { setError("This login link is invalid."); return; }
        api.post("/auth/magic/consume", { token })
            .then(async () => {
                // Re-read /auth/me with the now-set session cookie. refresh() bumps
                // the internal authSeq so the stale mount-time 401 can't clobber us.
                const u = await refresh();
                const next = new URLSearchParams(window.location.search).get("next") || "";
                // only our own paths (no //host or scheme) to avoid open redirects
                const safe = /^\/(?!\/)[A-Za-z0-9/_\-?=&.%]*$/.test(next) ? next : "/dashboard";
                if (u) navigate(safe, { replace: true });
                else setError("We signed you in but couldn't load your account. Please try logging in.");
            })
            .catch((e) => setError(e?.response?.data?.detail || "This login link could not be used."));
    }, [navigate, refresh]);

    return (
        <div className="min-h-screen bg-cream grid place-items-center px-6" data-testid="magic-login-page">
            <div className="w-full max-w-md bg-white border border-[#e8e1d4] p-10 text-center">
                <div className="flex justify-center mb-6"><Logo size="sm" withTagline={false} /></div>
                {error ? (
                    <>
                        <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
                        <h1 className="mt-4 font-display font-semibold text-xl">Link problem</h1>
                        <p className="mt-2 text-sm text-neutral-500" data-testid="magic-login-error">{error}</p>
                        <button onClick={() => navigate("/login")} className="btn-primary mt-6 w-full" data-testid="magic-login-to-login">Go to login</button>
                    </>
                ) : (
                    <>
                        <Loader2 className="h-10 w-10 text-brand-gold mx-auto animate-spin" />
                        <h1 className="mt-4 font-display font-semibold text-xl">Signing you in…</h1>
                        <p className="mt-2 text-sm text-neutral-500">Just a moment while we open your dashboard.</p>
                    </>
                )}
            </div>
        </div>
    );
}
